// Snelle hover-tooltip — vervangt de trage native `title`. Gebruik:
//   <div @mousemove=${tipMove} @mouseleave=${tipHide}>
//     <div data-tip="0–50 cm · zand"></div> ...
//   </div>
// Eén gedeeld tooltip-element op document.body (buiten de shadow DOM), gepositioneerd via
// viewport-coördinaten. Werkt overal hetzelfde (kaartpopup, boringenbalk, afwerkingbalk, …).
//
// TWEE STANDEN:
//   standaard  — volgt de cursor. Goed in dichte tabellen en balken, waar het element zelf
//                te klein of te lang is om aan te hangen.
//   data-tip-side="right" — hangt VAST aan het element: rechts ervan, verticaal gecentreerd,
//                met een pijltje ernaar toe. Nodig voor de navigatiebalk: een cursorvolgende
//                tooltip valt daar half over de balk zelf, en dan lijkt het label ergens
//                tussen twee iconen te horen in plaats van bij het icoon dat je aanwijst.
//
//                Staat er een ouder met `data-tip-anchor`, dan komt de HORIZONTALE plek van
//                die ouder. In de navigatiebalk is een item een vierkant van 40px in een balk
//                van 68px: hang je aan het item, dan begint de tooltip nog binnen de balk.
//                De verticale plek blijft van het item zelf — anders wijst het pijltje naar
//                het midden van de balk in plaats van naar het icoon.
//
// RIJEN — `data-tip-rows` (JSON) naast `data-tip`: een tooltip met benoemde velden en een actie,
//   { "rows": [["Monster", "A01-3"], ["Diepte", "0–50 cm"]], "action": { "text": "…", "kind": "add" } }
//   `kind`: add (groen), remove (rood) of info (grijs). `data-tip` blijft de platte tekst; die
//   bepaalt ook of er een tooltip is. Alles gaat via textContent, nooit als HTML.
//
// `white-space: pre-line` en niet `nowrap`: een \n in data-tip wordt zo een echte nieuwe
// regel, nodig zodra een tooltip meerdere meldingen opsomt. Meteen ook de oplossing voor
// lange teksten -- met nowrap won de tekst het van max-width en liep hij het scherm uit.

const BG = '#1f2933';
/** Afstand tot de rand waar de tooltip aan hangt; het pijltje is 6px, dus 10 laat 4px lucht. */
const ANCHOR_GAP = 10;

let _tip = null;
let _text = null;
let _arrow = null;
let _richSource = null;

const ACTION_COLORS = {
  add: { bg: '#4b9d35', fg: '#fff' },
  remove: { bg: '#c0392b', fg: '#fff' },
  info: { bg: 'rgba(255,255,255,.14)', fg: '#e5e9ec' },
};

/** Benoemde velden onder elkaar (label grijs, waarde wit), met de actie als knopje eronder. */
function renderRich({ rows = [], action = null }) {
  _text.textContent = '';
  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:auto 1fr;gap:3px 12px;white-space:normal;';
  for (const [label, value] of rows) {
    const l = document.createElement('span');
    l.style.cssText = 'color:#9aa5b1;font-weight:600;';
    l.textContent = label;
    const v = document.createElement('span');
    v.style.cssText = 'color:#fff;font-weight:700;';
    v.textContent = value;
    grid.append(l, v);
  }
  _text.appendChild(grid);
  if (action?.text) {
    const c = ACTION_COLORS[action.kind] || ACTION_COLORS.info;
    const a = document.createElement('div');
    a.style.cssText = `margin-top:8px;padding:4px 9px;border-radius:6px;display:inline-block;font-weight:800;font-size:12px;background:${c.bg};color:${c.fg};white-space:normal;`;
    a.textContent = action.text;
    _text.appendChild(a);
  }
}

function ensureTip() {
  if (_tip) return _tip;
  _tip = document.createElement('div');
  _tip.style.cssText = `position:fixed;z-index:99999;pointer-events:none;display:none;background:${BG};color:#fff;font:600 12.5px system-ui,-apple-system,sans-serif;padding:7px 12px;border-radius:7px;white-space:pre-line;line-height:1.4;max-width:300px;box-shadow:0 3px 12px rgba(0,0,0,0.32);`;
  // Pijltje en tekst als losse kinderen: met textContent op de tooltip zelf zou het pijltje
  // bij elke nieuwe tekst weer weg zijn.
  _arrow = document.createElement('div');
  _arrow.style.cssText = `position:absolute;left:-6px;top:50%;transform:translateY(-50%);width:0;height:0;border-top:6px solid transparent;border-bottom:6px solid transparent;border-right:6px solid ${BG};display:none;`;
  _text = document.createElement('span');
  _tip.appendChild(_arrow);
  _tip.appendChild(_text);
  document.body.appendChild(_tip);
  // Weg bij een klik en bij een paginawissel. Verdwijnt het element onder de muis -- je klikt
  // een rij aan en de pagina wisselt -- dan komt er nooit een mouseleave, en bleef de tooltip
  // over de volgende pagina hangen. Blijft de muis staan op iets met een tip, dan zet de
  // volgende mousemove hem gewoon terug.
  window.addEventListener('click', tipHide, true);
  window.addEventListener('popstate', tipHide);
  return _tip;
}

/** Toon/positioneer de tooltip voor het dichtstbijzijnde element met een `data-tip`. */
export function tipMove(e) {
  const el = e.target && e.target.closest ? e.target.closest('[data-tip]') : null;
  const text = el && el.getAttribute('data-tip');
  if (!text) { tipHide(); return; }
  const t = ensureTip();
  const rows = el.getAttribute('data-tip-rows');
  if (rows !== _richSource) {
    _richSource = rows;
    let rich = null;
    try { rich = rows ? JSON.parse(rows) : null; } catch (_) { rich = null; }
    if (rich) renderRich(rich); else _text.textContent = text;
  }
  if (!rows) _text.textContent = text;
  t.style.display = 'block';

  // Verankerd aan het element (navigatiebalk): rechts ernaast, op halve hoogte.
  if (el.getAttribute('data-tip-side') === 'right') {
    const item = el.getBoundingClientRect();
    // Horizontaal vanaf de omhullende balk (indien aanwezig), verticaal vanaf het item.
    const anchor = el.closest && el.closest('[data-tip-anchor]');
    const right = anchor ? anchor.getBoundingClientRect().right : item.right;
    const r = t.getBoundingClientRect();
    _arrow.style.display = 'block';
    t.style.left = `${right + ANCHOR_GAP}px`;
    // Binnen het scherm houden, ook als het item bovenaan of onderaan de balk staat.
    const top = item.top + item.height / 2 - r.height / 2;
    t.style.top = `${Math.min(Math.max(6, top), window.innerHeight - r.height - 6)}px`;
    return;
  }

  _arrow.style.display = 'none';
  const pad = 12;
  const r = t.getBoundingClientRect();
  let x = e.clientX + pad, y = e.clientY + pad;
  if (x + r.width > window.innerWidth - 6) x = e.clientX - r.width - pad;
  if (y + r.height > window.innerHeight - 6) y = e.clientY - r.height - pad;
  t.style.left = `${Math.max(6, x)}px`;
  t.style.top = `${Math.max(6, y)}px`;
}

export function tipHide() { if (_tip) _tip.style.display = 'none'; }
